import { useEffect, useState, type ReactNode } from 'react';
import './Toast.css';

export interface ToastProps {
  message: string;
  visible: boolean;
  icon?: ReactNode;
  /**
   * Colour treatment. Defaults to `info`.
   *
   * As on `Alert`, the tone is reinforcement — the message text has to carry
   * the meaning on its own, since a screen reader is read the words and not
   * the colour.
   */
  tone?: 'info' | 'success' | 'danger';
}

export function Toast({ message, visible, icon, tone = 'info' }: ToastProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      return undefined;
    }
    setIsVisible(false);
    const timer = setTimeout(() => setShouldRender(false), 300);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`toast-notification toast-notification--${tone} ${
        isVisible ? 'visible' : 'hiding'
      }`}
      // An error toast interrupts; a confirmation waits its turn.
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      {icon && <span className="toast-icon">{icon}</span>}
      <span>{message}</span>
    </div>
  );
}
